import { h1bAuditBase, type H1bEcosMetadataAudit } from "./h1bEcosMetadataModel.ts";
import { dailyAnalysisClient } from "./dailyAnalysisClient.ts";
import { DAILY_ANALYSIS_ENABLED } from "./dailyAnalysisConfig.ts";

const PROXY_URL = "/api/ecos";

interface ProxyResponse {
  ok?: boolean;
  audit?: H1bEcosMetadataAudit;
  error?: string;
}

export type { EcosMetadataRow, H1bMetadataCandidate, H1bEcosMetadataAudit, H1bMortgageBalanceAssessment, H1bMortgageBalanceCandidateAssessment } from "./h1bEcosMetadataModel.ts";
export {
  H1B_DEFAULT_TABLE_CODES,
  H1B_TABLE_TERMS,
  H1B_ITEM_TERMS,
  H1B_DESCENDANT_ROOT_TERMS,
  H1B_MAX_AUDIT_TABLES,
  compactH1bMetadataRow,
  selectH1bCandidateTables,
  filterH1bCandidateItems,
  summarizeH1bMonthlyDimensions,
  assessH1bMortgageBalanceCandidates
} from "./h1bEcosMetadataModel.ts";

export async function collectH1bEcosMetadataAudit(signal?: AbortSignal): Promise<H1bEcosMetadataAudit> {
  if (DAILY_ANALYSIS_ENABLED) return dailyAnalysisClient.read<H1bEcosMetadataAudit>("ecos-audit", signal);
  // Metadata audit is diagnostic evidence. Do not allow a previous deployment's
  // CDN/browser response to survive into a new diagnostic ZIP. A timestamped
  // cache key plus no-store makes each explicit audit request reach the current
  // Pages Function while the ordinary ECOS items/search routes can retain their
  // existing cache policy.
  const response = await fetch(`${PROXY_URL}?op=h1bAudit&fresh=${Date.now()}`, {
    method: "GET",
    headers: { accept: "application/json" },
    cache: "no-store",
    signal
  });
  const text = await response.text();
  let body: ProxyResponse;
  try {
    body = JSON.parse(text) as ProxyResponse;
  } catch {
    throw new Error(`H1B ECOS metadata audit returned non-JSON (${response.status})`);
  }
  if (!response.ok || !body.ok || !body.audit) {
    throw new Error(body.error || `H1B ECOS metadata audit failed (${response.status})`);
  }
  return body.audit;
}


export function unsupportedH1bMetadataAudit(reason = "Central consumer mode intentionally does not run provider-level ECOS metadata discovery."): H1bEcosMetadataAudit {
  const collectedAt = new Date().toISOString();
  return {
    status: "unsupported_in_central_mode",
    ...h1bAuditBase(collectedAt),
    purpose: reason,
    tableListTotal: null,
    selectedTableCount: 0,
    candidateCount: 0,
    truncatedCandidateTables: false,
    selectedTables: [],
    candidates: [],
    multiDimensionalTables: [],
    mortgageBalanceAssessment: {
      productionRule: "Provider metadata discovery is not run in central consumer mode. Gate C remains N/A until an exact official same-population mortgage-balance series is separately verified.",
      eligibleForSemanticReview: [],
      nearMisses: []
    },
    errors: []
  };
}

export function failedH1bMetadataAudit(error: unknown): H1bEcosMetadataAudit {
  const collectedAt = new Date().toISOString();
  return {
    status: "error",
    ...h1bAuditBase(collectedAt),
    tableListTotal: null,
    selectedTableCount: 0,
    candidateCount: 0,
    truncatedCandidateTables: false,
    selectedTables: [],
    candidates: [],
    multiDimensionalTables: [],
    mortgageBalanceAssessment: {
      productionRule: "No metadata was available for Gate C assessment.",
      eligibleForSemanticReview: [],
      nearMisses: []
    },
    errors: [{ scope: "server-h1bAudit", error: error instanceof Error ? error.message : String(error) }]
  };
}
